import React from 'react';
import CurrencyFormat from 'react-currency-format';

// The period total behind the number the drill-down was opened from, per source and together.
//
// The number in the report counts sports and games legs both, so a total covering one of them is a
// figure that reads as the whole period while covering half of it - and the reader has to switch
// segments to learn the other source's contribution. This is one call read once when the modal opens,
// so the row is the same whichever segment is open and cannot move as the segment changes.
//
// A leg with no activity is not a row (Andrew, 2026-09-22): a member whose period is games only was
// shown two all-zero lines above the one carrying the money, which is also what made the block look
// like it disagreed with the segment underneath it. And the Grand Total only earns its row when more
// than one segment is left to add up - with a single segment on screen it repeats that row exactly.
const Money = ({ value }) => (
  <CurrencyFormat
    value={value}
    decimalScale={2}
    displayType={'text'}
    thousandSeparator={true}
    fixedDecimalScale={true}
  />
);

/**
 * A leg with nothing on it: every figure zero or absent.
 *
 * <p>One figure is enough to keep the row: a games day whose only money is a bonus has no amount and no
 * turnover - a bonus carries no stake - and a real winning, so an amount-only test would hide money.</p>
 */
const isLegEmpty = (leg) =>
  !leg ||
  [
    leg.totalAmount,
    leg.totalTurnover,
    leg.totalCommission,
    leg.totalNetAmount,
    leg.totalWinning,
  ].every((figure) => !figure);

const BalanceGrandTotal = ({ total }) => {
  if (!total) return null;
  // WeGa sport is its own line, not folded into Sports: the two are separate populations (our own bets,
  // and the WeGa bets priced off the same stake settings) and each segment below lists one of them, so the
  // reader can tie each row here to a list. Sports alone was the shipped figure and it is own-sport only -
  // leaving it as the only sports line is what made a vendor whose activity is games and WeGa sport read
  // "Sports 0" above a Grand Total that visibly contained the money. The three lines now add up to the
  // Grand Total, which the server computes from the same three legs.
  const segments = [
    { label: 'Sports', leg: total.sports },
    { label: 'WeGa sport', leg: total.wegaSports },
    { label: 'Games', leg: total.games },
  ].filter((row) => !isLegEmpty(row.leg));

  // The Grand Total is the server's own sum of the three legs, so it keeps covering whatever the segment
  // lines dropped - except when one segment is all that is left, where it is that row over again. If no
  // segment line survives but the total is not empty, the total is the only row there is to show.
  const showGrandTotal = !isLegEmpty(total.combined) && segments.length !== 1;
  const legs = showGrandTotal ? [...segments, { label: 'Grand Total', leg: total.combined }] : segments;
  if (legs.length === 0) return null;

  return (
    <table className="table table-sm table-striped table-bordered balance-grand-total mb-2">
      {/* The same head the two bet lists wear, so the block reads as one table family with them. */}
      <thead className="text-center thead-dark">
        <tr>
          <th scope="col" />
          <th scope="col">Amount</th>
          <th scope="col">Turnover</th>
          <th scope="col">Comm</th>
          <th scope="col">Net Amt</th>
          <th scope="col">Winning</th>
        </tr>
      </thead>
      <tbody>
        {legs.map(({ label, leg }) => (
          <tr key={label} className={label === 'Grand Total' ? 'font-weight-bold' : ''}>
            <td className="text-nowrap">{label}</td>
            <td className="text-right">
              <Money value={leg.totalAmount} />
            </td>
            <td className="text-right">
              <Money value={leg.totalTurnover} />
            </td>
            <td className="text-right">
              <Money value={leg.totalCommission} />
            </td>
            <td className="text-right">
              <Money value={leg.totalNetAmount} />
            </td>
            <td className={`text-right ${leg.totalWinning < 0 ? 'text-danger' : ''}`}>
              <Money value={leg.totalWinning} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default BalanceGrandTotal;
